import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getVendorMembersRequest } from "../../../redux/vendor/members/action";
import {
    getCityListRequest,
    getCountyListRequest,
    getStateListRequest,
} from "../../../redux/admin/location/action";
import { getDateObjectWithValueFromMomentPayload } from "../../../helper/DataTimeHelpers";
import { getVendorMemberColumnsRequest } from "../../../redux/vendor/memberColumns/action";
import { exportVendorMembers } from "./memberUtils";
import { toColumns } from "./memberColumns";
import MemberFilterCard from "./MemberFilterCard";
import MemberTable from "./MemberTable";
import MemberCards from "./MemberCards";
import MemberFootBar from "./MemberFootBar";
import { ApiResponseMessage } from "../../../helper/tosterHelper";
import {
    VendorExportSvg,
    VendorSearchSvg,
    VendorFunnelSvg,
} from "../../../components/CommonSvg";
import { vendorHasRole } from "../../../utils/authUtils";
import {
    DANGER,
    VendorDownloadMemberExcelAccess,
    VendorViewMemberListAccess,
} from "../../../constant/commonConstants";

const MEMBER_STATUS = "Active";

const VendorMembers = ({
    getMemberList,
    getCityList,
    getStateList,
    getCountyList,
    cityList,
    stateList,
    countyList,
    memberList,
    totalRecords,
    columnConfig,
    getVendorMemberColumns,
}) => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [cityName, setCityName] = useState("");
    const [stateName, setStateName] = useState("");
    const [countyName, setCountyName] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [orderKey, setOrderKey] = useState("createdDate");
    const [orderBy, setOrderBy] = useState("ASC");
    const [pageSize, setPageSize] = useState(25);
    const [page, setPage] = useState(1);
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);

    useEffect(() => {
        if (!vendorHasRole(VendorViewMemberListAccess)) {
            navigate("/vendor/dashboard");
        }
    }, []);

    useEffect(() => {
        getCityList();
        getStateList();
        getCountyList();
        getVendorMemberColumns();
    }, []);

    const optionalColumns = useMemo(() => {
        const savedFields = (columnConfig || []).map((column) => column.field);
        const columns = toColumns(savedFields);
        return columns.length > 0 ? columns : [];
    }, [columnConfig]);

    const dispatchData = {
        searchText: searchTerm ? searchTerm : "",
        startDate: startDate
            ? getDateObjectWithValueFromMomentPayload(startDate)
            : "",
        endDate: endDate ? getDateObjectWithValueFromMomentPayload(endDate) : "",
        state: stateName,
        city: cityName,
        county: countyName,
        status: MEMBER_STATUS,
        pagination: {
            limit: pageSize,
            page: page,
            orderKey: orderKey,
            orderBy: orderBy,
        },
    };

    useEffect(() => {
        getMemberList(dispatchData);
    }, [
        pageSize,
        page,
        searchTerm,
        orderKey,
        orderBy,
        cityName,
        stateName,
        countyName,
        startDate,
        endDate,
    ]);

    useEffect(() => {
        setPage(1);
    }, [cityName, stateName, countyName, startDate, endDate]);

    const activeFilterCount = [
        cityName,
        stateName,
        countyName,
        startDate,
        endDate,
    ].filter(Boolean).length;

    const handleClearFilters = () => {
        setCityName("");
        setStateName("");
        setCountyName("");
        setStartDate("");
        setEndDate("");
    };

    const rows = memberList;

    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const currentPage = Math.min(page, totalPages);

    const pageNumbers = [];
    for (let p = Math.max(1, currentPage - 2); p <= Math.min(totalPages, currentPage + 2); p += 1) {
        pageNumbers.push(p);
    }

    const handleSort = (key) => {
        if (orderKey === key) {
            setOrderBy(orderBy === "ASC" ? "DESC" : "ASC");
        } else {
            setOrderKey(key);
            setOrderBy("ASC");
        }
        setPage(1);
    };

    const handleExport = async () => {
        const result = await exportVendorMembers({
            ...dispatchData,
            columns: optionalColumns.map((column) => column.key),
        });
        if (!result.status) {
            ApiResponseMessage(result.message, DANGER);
        }
    };

    const viewMember = (member) =>
        navigate(`/vendor/members/view/${member.hraMemberId}`);

    return (
        <div className="vp-page">
            <div className="vp-mhead-top">
                <div>
                    <div className="vp-dtitle">Member Stores</div>
                </div>
                <div className="vp-dtoolbar">
                    {vendorHasRole(VendorDownloadMemberExcelAccess) && (
                        <button
                            type="button"
                            className="vp-upload-btn"
                            onClick={handleExport}
                        >
                            <VendorExportSvg />
                            Export
                        </button>
                    )}

                    <div className="vp-dsearch">
                        <VendorSearchSvg />
                        <input
                            type="text"
                            value={searchTerm}
                            placeholder="Search members…"
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPage(1);
                            }}
                        />
                    </div>

                    <div className="vp-fmenu-wrap">
                        <button
                            type="button"
                            className={`vp-dfilter-select icon-btn${activeFilterCount ? " has-filters" : ""}`}
                            title="Filter members"
                            onClick={() => setFilterMenuOpen((prev) => !prev)}
                        >
                            <VendorFunnelSvg />
                        </button>
                    </div>
                </div>
            </div>

            {filterMenuOpen && (
                <MemberFilterCard
                    stateList={stateList}
                    cityList={cityList}
                    countyList={countyList}
                    stateName={stateName}
                    cityName={cityName}
                    countyName={countyName}
                    onStateChange={setStateName}
                    onCityChange={setCityName}
                    onCountyChange={setCountyName}
                    onClear={handleClearFilters}
                    onClose={() => setFilterMenuOpen(false)}
                />
            )}

            <MemberTable
                rows={rows}
                optionalColumns={optionalColumns}
                onSort={handleSort}
                onView={viewMember}
            />

            <MemberCards rows={rows} onView={viewMember} />

            {rows.length === 0 && (
                <div className="vp-mempty">No members match your search.</div>
            )}

            <MemberFootBar
                pageSize={pageSize}
                totalRecords={totalRecords}
                currentPage={currentPage}
                totalPages={totalPages}
                pageNumbers={pageNumbers}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                }}
                onPageChange={setPage}
            />
        </div>
    );
};

const mapStateToProps = ({
    VendorMembers: vendorMembers,
    location,
    VendorMemberColumns,
}) => {
    let memberList = vendorMembers?.list?.length > 0 ? vendorMembers.list : [];
    let totalRecords = vendorMembers?.totalRecords || 0;
    let cityList = location?.getCity?.list?.length > 0 ? location.getCity.list : [];
    let stateList =
        location?.getState?.list?.length > 0 ? location.getState.list : [];
    let countyList =
        location?.getCounty?.list?.length > 0 ? location.getCounty.list : [];

    let columnConfig =
        VendorMemberColumns?.columns?.list?.columnsSelected || [];

    return {
        memberList,
        totalRecords,
        cityList,
        stateList,
        countyList,
        columnConfig,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getMemberList: (data) => dispatch(getVendorMembersRequest(data)),
        getVendorMemberColumns: () => dispatch(getVendorMemberColumnsRequest()),
        getCityList: () => dispatch(getCityListRequest()),
        getStateList: () => dispatch(getStateListRequest()),
        getCountyList: () => dispatch(getCountyListRequest()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(VendorMembers);
